import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, PageHeader, StatusPill, EmptyState, Field, Select } from '../components/ui';
import { SearchInput } from '../components/SearchInput';
import { Pagination } from '../components/Pagination';
import { Dialog } from '../components/dialogs/Dialog';
import { OduCollectionPicker } from '../components/OduCollectionPicker';
import { MediaAPI } from '../services/endpoints/media';
import { usePagedQuery } from '../hooks/usePagedQuery';
import type { Media, MediaType, Page, PurchaseOption } from '../types/api';
import { PURCHASE_OPTIONS, DEFAULT_PURCHASE_OPTION } from '../data/media';
import { formatDate, formatDuration } from '../utils/format';
import './MediaListPage.css';
import './common.css';

interface Props {
  mediaType: MediaType;
}

const COPY: Record<MediaType, {
  eyebrow: string;
  titleWord: string;
  lede: string;
  newLabel: string;
  dialogTitle: string;
  placeholder: string;
  emptyHint: string;
}> = {
  AUDIO: {
    eyebrow: '02 — AUDIO',
    titleWord: 'audio library',
    lede: 'Every audio item, indexed by title and collection. Click a row to edit metadata, upload the file, or publish.',
    newLabel: '+ New audio',
    dialogTitle: 'New audio entry',
    placeholder: 'Search audio by title, collection, transcript…',
    emptyHint: 'The audio library is empty. Start with a new entry.',
  },
  VIDEO: {
    eyebrow: '03 — VIDEO',
    titleWord: 'video library',
    lede: 'Every video item, indexed by title and collection. Click a row to edit metadata, upload the file, or publish.',
    newLabel: '+ New video',
    dialogTitle: 'New video entry',
    placeholder: 'Search video by title, collection, transcript…',
    emptyHint: 'The video library is empty. Start with a new entry.',
  },
};

export function MediaListPage({ mediaType }: Props) {
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const copy = COPY[mediaType];

  const fetcher = useCallback(async ({ page, q }: { page: number; q: string }): Promise<Page<Media>> => {
    const res = await MediaAPI.search(q || 'ADMIN_APP', page, mediaType);
    return { data: res.data || [], page: res.page ?? page, size: 25, totalCount: res.totalCount, totalPages: res.totalPages };
  }, [mediaType]);

  const query = usePagedQuery<Media, Record<string, never>>({
    fetcher,
    initialFilters: {},
    pageSize: 25,
  });

  return (
    <div>
      <PageHeader
        eyebrow={copy.eyebrow}
        title={<>The <em>{copy.titleWord}</em>.</>}
        lede={copy.lede}
        actions={
          <>
            <SearchInput value={query.q} onChange={query.setQ} placeholder={copy.placeholder} />
            <Button tone="ember" onClick={() => setCreateOpen(true)}>{copy.newLabel}</Button>
          </>
        }
      />

      {query.error && (
        <div className="nb-error-banner">
          {query.error}
        </div>
      )}

      {query.loading && !query.data.length ? (
        <Card><EmptyState title="Loading…" /></Card>
      ) : query.data.length === 0 ? (
        <Card>
          <EmptyState
            title="No media found"
            hint={query.q ? `Nothing matches "${query.q}".` : copy.emptyHint}
            action={<Button tone="ember" onClick={() => setCreateOpen(true)}>{copy.newLabel}</Button>}
          />
        </Card>
      ) : (
        <Card padded={false}>
          <table className="nb-media-list">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Collection</th>
                <th>Author</th>
                <th>Type</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((m, i) => (
                <tr key={m.id} onClick={() => nav(`/media/${m.id}`)} role="link">
                  <td className="nb-media-list__num">{String(query.page * query.pageSize + i + 1).padStart(3, '0')}</td>
                  <td>
                    <div className="nb-media-list__title">
                      {m.collectionName || <em>(no collection)</em>}
                      {m.pinned ? <>&nbsp;<StatusPill tone="ember">Pinned</StatusPill></> : m.featured ? <>&nbsp;<StatusPill tone="moss">Featured</StatusPill></> : null}
                    </div>
                    {m.subtitle && <div className="nb-media-list__desc">{m.subtitle}</div>}
                    {!m.subtitle && m.description && <div className="nb-media-list__desc">{m.description.slice(0, 80)}{m.description.length > 80 ? '…' : ''}</div>}
                  </td>
                  <td>{m.author || m.creatorName || '—'}</td>
                  <td>{m.type || mediaType}</td>
                  <td className="nb-media-list__num">{formatDuration(m.duration)}</td>
                  <td><MediaStatusPill status={m.status} /></td>
                  <td className="nb-media-list__num">{formatDate(m.createdTimestamp)}</td>
                  <td className="nb-media-list__num">{formatDate(m.lastModifiedTimestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={query.page}
            pageSize={query.pageSize}
            total={query.totalCount}
            hasMore={(query.totalCount === undefined) ? query.data.length >= query.pageSize : undefined}
            onPage={query.setPage}
          />
        </Card>
      )}

      <CreateMediaDialog
        open={createOpen}
        mediaType={mediaType}
        dialogTitle={copy.dialogTitle}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => nav(`/media/${id}`)}
      />
    </div>
  );
}

function MediaStatusPill({ status }: { status?: string }) {
  if (status === 'PUBLISHED')     return <StatusPill tone="moss">Published</StatusPill>;
  if (status === 'UNPUBLISHED')   return <StatusPill tone="ember">Unpublished</StatusPill>;
  if (status === 'FILE_UPLOADED') return <StatusPill tone="brass">Ready</StatusPill>;
  if (status === 'DELETED')       return <StatusPill tone="rust">Deleted</StatusPill>;
  return <StatusPill tone="brass">New</StatusPill>;
}

function CreateMediaDialog({ open, mediaType, dialogTitle, onClose, onCreated }: {
  open: boolean;
  mediaType: MediaType;
  dialogTitle: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [purchaseOption, setPurchaseOption] = useState<PurchaseOption>(DEFAULT_PURCHASE_OPTION);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!collectionName) return;
    setBusy(true); setError(null);
    try {
      // Created as a draft → transcode completion will NOT auto-publish.
      // The admin reviews, then publishes manually via the Publish toggle.
      const m = await MediaAPI.save({
        title: title.trim() || undefined,
        collectionName,
        type: mediaType,
        status: 'NEW',
        draft: true,
        purchaseOption,
      });
      onCreated(m.id);
      onClose();
      setTitle('');
      setCollectionName('');
      setPurchaseOption(DEFAULT_PURCHASE_OPTION);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button tone="ember" onClick={create} disabled={busy || !collectionName}>Create &amp; continue</Button>
        </>
      }
    >
      {error && <div className="nb-error-banner nb-error-banner--sm">{error}</div>}
      <OduCollectionPicker value={collectionName} onChange={setCollectionName} />
      <label className="nb-field" style={{ marginTop: 16 }}>
        <span className="nb-field__label">Title <span className="nb-field__hint" style={{ display: 'inline' }}>(optional)</span></span>
        <input className="nb-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title of the piece" />
      </label>
      <div style={{ marginTop: 16 }}>
        <Field label="Access" hint={PURCHASE_OPTIONS.find(o => o.value === purchaseOption)?.hint}>
          <Select value={purchaseOption} onChange={e => setPurchaseOption(e.target.value as PurchaseOption)}>
            {PURCHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
